import { CustomAxiosOptions, RenewTokenFn } from './custom-axios';
import { TOKEN_STORAGE_KEY } from './custom-axios.constants';

let inFlightRenewal: Promise<string | null | undefined> | null = null;

/**
 * Coalesces concurrent token renewals so a burst of 401s (e.g. many queries
 * firing on a cold boot with a stale token) triggers a single OIDC silent renew,
 * not one iframe round-trip per failed request. Resolves nullish if the renewal fails.
 */
export const coalesceTokenRenewal = (renew: RenewTokenFn) => {
  if (!inFlightRenewal) {
    inFlightRenewal = Promise.resolve()
      .then(renew)
      .catch(() => null)
      .finally(() => {
        inFlightRenewal = null;
      });
  }
  return inFlightRenewal;
};

/**
 * True when the request URL's origin matches one of the origins that should be
 * sent with credentials. Relative URLs never match: they resolve to the page's
 * own origin, and the browser already sends cookies on same-origin requests
 * regardless of withCredentials (that flag only governs cross-origin requests).
 */
export const matchesOriginWithCredentials = (
  url: string | undefined,
  origins: string[] | undefined,
) => {
  if (!url || !origins?.length) return false;

  let target: URL;
  try {
    target = new URL(url);
  } catch {
    return false;
  }

  return origins.some((origin) => {
    try {
      return new URL(origin).origin === target.origin;
    } catch {
      return false;
    }
  });
};

export const handleTokenFromConfig = (config?: CustomAxiosOptions) => {
  if (!config) {
    return getTokenFromStorage('session');
  }

  const { authToken, tokenFromStorage } = config;
  return authToken || getTokenFromStorage(tokenFromStorage || 'session');
};

/**
 * Get the auth token from storage.
 * For now, it's allowing to get from local or session storage.
 * Ideally, it should only be from session storage. But leaving this for compatibility.
 *
 * Use this function if the app is not implementing the @xemplo/auth-provider
 */
const getTokenFromStorage = (tokenFromStorage: 'local' | 'session') => {
  const result =
    tokenFromStorage === 'local'
      ? localStorage.getItem(TOKEN_STORAGE_KEY)
      : sessionStorage.getItem(TOKEN_STORAGE_KEY);

  return result || '';
};
